/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useHeaderData } from '@/features/header/api/getHeader';
import { useAppState } from '@/stores/AppState';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAppData } from '@/hooks/useAppData';

export type VertexPersonalizationParams = {
  visitorId: string; // From AppState (utag_main cookie)
  userId: string; // UNHASHED MyProfile.customerID or cookie (will be hashed in API function)
  shouldInjectParams: boolean; // Whether to inject params (experimentId === 'vertex' OR 'elasticsearch')
  isMyProfileReady: boolean; // For blocking API calls until MyProfile loads
};

export type VertexPersonalizationParamsDependencies = {
  userAuthenticated: boolean;
  visitorId: string | undefined;
  acesEnabled: boolean;
  experimentId: string | null | undefined;
  headerData: { myAccountMap?: { customerID?: string } } | null | undefined;
  isMyProfileReady: boolean;
};

export function getVertexPersonalizationParams(
  deps: VertexPersonalizationParamsDependencies,
): VertexPersonalizationParams {
  const { userAuthenticated, visitorId, acesEnabled, experimentId, headerData, isMyProfileReady } =
    deps;

  const shouldInjectParams =
    acesEnabled && (experimentId === 'vertex' || experimentId === 'elasticsearch');

  const shouldFetchMyProfile = userAuthenticated && shouldInjectParams;

  // Case 1: Not a personalization experiment - skip params
  if (!shouldInjectParams) {
    return {
      visitorId: '',
      userId: '',
      shouldInjectParams: false,
      isMyProfileReady: true,
    };
  }

  // Case 2: Authenticated users - use UNHASHED MyProfile.customerID (will be hashed in API)
  if (shouldFetchMyProfile) {
    return {
      visitorId: visitorId ?? '',
      userId: headerData?.myAccountMap?.customerID ?? '',
      shouldInjectParams: true,
      isMyProfileReady,
    };
  }

  // Case 3: Unauthenticated users - use UNHASHED cookie fallback (will be hashed in API)
  return {
    visitorId: visitorId ?? '',
    userId: '',
    shouldInjectParams: true,
    isMyProfileReady: true,
  };
}

export function useVertexPersonalizationParams() {
  const { state } = useAppState();
  const { acesEnabled } = useAcesFlags();
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const experimentId = useExperimentId();

  const { data: headerData, isSuccess } = useHeaderData();

  return getVertexPersonalizationParams({
    visitorId: state.visitorId ?? '',
    acesEnabled,
    experimentId,
    headerData,
    userAuthenticated,
    isMyProfileReady: isSuccess && !!headerData,
  });
}
